import { AutoComplete, Spin } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { IoMdCloseCircle } from 'react-icons/io';

import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { EnvironmentOutlined } from '@ant-design/icons';
import {
  AUTOCOMPLETE_TYPE,
  removeDuplicateAutocomplete,
} from '@skygroup/shared/API/autoCompleteApi/autoCompleteUtils';
import {
  useGetCity,
  useGetCityAutoComplete,
  useGetHotelAutoComplete,
} from '@skygroup/shared/API/autoCompleteApi/autoCompleteApi';
import { HotelElement } from './HotelElement';
import AdditionalDepartureDropdown from './additionalDepartureDropdown/AdditionalDepartureDropdown';
import CustomAutoCompleteAirport from './CustomAutoCompleteAirport';
import ArrowSVG from '../../assets/ArrowSVG';
import { TenantTsType } from '@skygroup/shared/constants/tenantTs';
import { getTsTypeByIdentifier } from '@skygroup/shared/utils/flightIdentifier';

interface CustomOptionType {
  label: string;
  options: { value: string; type: 'city' | 'hotel'; label: any }[];
  localDeparture?: string | null;
  setLocalDeparture?: (v: string) => void;
}

export const CityElement = ({
  name,
  country,
  distance,
}: {
  name: string;
  country: string;
  distance?: number;
}) => {
  return (
    <div className="auto-complete-city-element">
      <div
        className="type-icon-wrapper"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 5,
        }}
      >
        <EnvironmentOutlined />
        {distance ? (
          <span className="distance">
            <b>{distance.toFixed(2)}</b> km
          </span>
        ) : null}
      </div>
      <span>{`${name} (${country})`}</span>
    </div>
  );
};
interface Props extends React.ComponentProps<typeof AutoComplete> {
  width?: string;
  placeholder?: string;
  // options: BaseOptionType[] | DefaultOptionType[] | CustomOptionType[];
  defaultOptions: any;
  selectedCityFromConfig?: {
    value: string;
    country: string;
    city: string;
  };
  locationId: string | undefined;
  hotelId?: string | null;
  allowClear?: boolean;
  isHotel?: boolean;
  isDynamicPackageSearch?: boolean;
  addDepartureDropdown?: boolean;
  departureDropdownId?: string;
  localDeparture?: string | null;
  setLocalDeparture?: (v: string) => void;
  departureFromConfig?: any;
}

const CustomAutoCompleteCity: React.FC<Props> = ({
  width,
  placeholder,
  defaultOptions,
  selectedCityFromConfig,
  locationId,
  hotelId,
  allowClear = true,
  isHotel,
  isDynamicPackageSearch,
  addDepartureDropdown = false,
  departureDropdownId,
  localDeparture = null,
  setLocalDeparture,
  departureFromConfig = null,
  ...autoCompleteProps
}) => {
  const { searchDatePicker: t } = useSelector(
    (state: RootState) => state.config.translations
  );
  const [searchValue, setSearchValue] = useState('');
  const formattedCityOptions = defaultOptions
    ?.map((option: any) => {
      return {
        value: option.value,
        iata_code: option?.iata_code,
        type: 'city',
        label: <CityElement name={option.city} country={option.country} />,
      };
    })
    ?.filter((option: any) => !!option?.iata_code);

  const formattedHotelOptions = defaultOptions?.map((option: any) => {
    return {
      value: option.value,
      type: 'hotel',
      label: <HotelElement {...option} />,
    };
  });

  const [formattedOptions, setFormattedOptions] = useState<
    {
      label: string;
      options: {
        value: string;
        type: 'city' | 'hotel';
        iata_code?: string;
        label: any;
      }[];
    }[]
  >([]);

  const [selectedSearchElement, setSelectedSearchElement] = useState<any>(
    selectedCityFromConfig
      ? {
          value: selectedCityFromConfig?.value,
          label: (
            <CityElement
              name={selectedCityFromConfig?.city}
              country={selectedCityFromConfig?.country}
            />
          ),
        }
      : null
  );

  useEffect(() => {
    let returnVal = [];
    if (formattedCityOptions && formattedCityOptions.length > 0) {
      returnVal.push({
        label: t.city,
        options: formattedCityOptions,
      });
    }
    // Temporary restricting hotel search for dynamic packages mode
    // because it requires a different flow and API call
    // instead of navigating to the hotel view page, we have to navigate dynamic packages page
    // and use cheapest flight api
    if (
      // !isDynamicPackageSearch &&
      formattedHotelOptions &&
      formattedHotelOptions.length > 0
    ) {
      returnVal.push({
        label: t.hotel,
        options: formattedHotelOptions,
      });
    }
    if (returnVal.length > 0) {
      setFormattedOptions(returnVal);
    }
  }, [formattedCityOptions, formattedHotelOptions]);

  const [isShowSearchValue, setIsShowSearchValue] = useState(false);
  const {
    currentLang,
    config: { siteConfig },
    direction,
  } = useSelector((data: RootState) => data.config);
  const { data: cityData, isFetching: isCityDataFetching } =
    useGetCityAutoComplete(searchValue, currentLang, !isHotel);

  const { data: hotelData, isFetching: isHotelDataFetching } =
    useGetHotelAutoComplete({
      searchVal: searchValue,
      currentLang,
      enable: isHotel,
    });

  const data = useMemo(() => {
    if (!cityData && !hotelData) return;
    if (cityData && !isHotel) {
      return { data: { city: cityData?.data, hotel: [] } };
      // const locationFilteredByCitiesOnly = cityData?.data?.length ? cityData?.data?.filter(
      //   (loc: any) => loc.type === 'city'
      // ) : [];
      // return { data: { city: locationFilteredByCitiesOnly, hotel: [] } };
    }
    return hotelData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelData, cityData]);

  const isFetching = isHotel ? isHotelDataFetching : isCityDataFetching;

  const { data: singleCity, refetch: refetchSingleCity } =
    useGetCity(locationId);

  useEffect(() => {
    !selectedCityFromConfig && locationId && refetchSingleCity();
  }, []);

  useEffect(() => {
    if (singleCity?.data && !selectedSearchElement) {
      setSelectedSearchElement({
        value: singleCity.data.location_id,
        label: (
          <CityElement
            name={singleCity.data.name}
            country={singleCity.data.country}
          />
        ),
      });
    }
  }, [singleCity]);

  useEffect(() => {
    if (
      !searchValue ||
      (data?.data?.city?.length < 1 && data?.data?.hotel?.length < 1)
    ) {
      setFormattedOptions([]);
    }

    let cities: {
      value: any;
      type: 'city';
      iata_code: string;
      label: JSX.Element;
    }[] = [];
    let hotels: {
      value: any;
      type: 'hotel';
      label: JSX.Element;
    }[] = [];

    let returnVal: {
      label: string;
      options: {
        value: string;
        type: 'hotel' | 'city';
        iata_code?: string;
        label: any;
      }[];
    }[] = [];

    if (data?.data?.city && data?.data?.city?.length > 0) {
      cities = removeDuplicateAutocomplete(
        data?.data?.city,
        AUTOCOMPLETE_TYPE.CITY
      ).map((city: any) => {
        return {
          value: city.location_id,
          type: 'city',
          iata_code: city.iata_code,
          label: <CityElement name={city.name} country={city.country} />,
        };
      });

      if (isDynamicPackageSearch && cities?.length > 0) {
        // cities = cities.filter((city: any) => {
        //   return !!city.iata_code;
        // });
      }

      returnVal.push({
        label: t.city,
        options: cities,
      });
    }

    if (
      // !isDynamicPackageSearch &&
      data?.data?.hotel &&
      data?.data?.hotel?.length > 0
    ) {
      hotels = removeDuplicateAutocomplete(
        data?.data?.hotel,
        AUTOCOMPLETE_TYPE.HOTEL_IDENTIFIER
      ).map((hotel: any) => {
        const isSkyInventory =
          getTsTypeByIdentifier({
            identifier: hotel?.identifier,
            type: 'hotel',
          }) === TenantTsType.SKY;

        return {
          value: hotel.identifier,
          type: 'hotel',
          className: isSkyInventory ? 'is-sky-inventory' : '',
          label: <HotelElement {...hotel} />,
        }
      });

      returnVal.push({
        label: t.hotel,
        options: hotels,
      });
    }
    setFormattedOptions(returnVal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchValue]);

  return (
    <div className="custom-auto-complete-wrapper">
      <div className="custom-auto-complete" style={{ width }}>
        <div className="departure-header" id={departureDropdownId}>
          {/* render original AdditionalDepartureDropdown when NOT in custom mode */}
          {addDepartureDropdown && (
            <AdditionalDepartureDropdown
              localDeparture={localDeparture}
              setLocalDeparture={setLocalDeparture}
              departureFromModuleConfig={departureFromConfig}
            />
          )}
          <span
            className={`placeholder ${
              addDepartureDropdown ? 'with-additional-dropdown' : ''
            }`}
          >
            {t.departure}
            {isFetching && <Spin />}
          </span>
        </div>

        <AutoComplete
          popupClassName={`custom-auto-complete-dropdown ${
            isFetching ? 'custom-auto-complete-loading' : ''
          }`}
          style={{ minWidth: 'unset' }}
          {...autoCompleteProps}
          dropdownMatchSelectWidth={300}
          clearIcon={<IoMdCloseCircle className="close-icon" size={20} />}
          allowClear={allowClear}
          onSelect={(value, wholeObject) => {
            setSelectedSearchElement(wholeObject);
            autoCompleteProps &&
              autoCompleteProps?.onSelect?.(value, wholeObject);
            setIsShowSearchValue(false);

            // @ts-ignore
            setSearchValue(wholeObject?.label?.props?.name);
          }}
          onFocus={(e) => {
            if (isShowSearchValue) {
              // setSearchValue('');
              setIsShowSearchValue(true);
            }
          }}
          onSearch={(e) => {
            setIsShowSearchValue(true);
            setSearchValue(e);
          }}
          onBlur={(e) => {
            if (!selectedSearchElement) {
              setIsShowSearchValue(true);
              // @ts-ignore
              // setSearchValue(e?.target?.value);
              return;
            }

            // const value = autoCompleteProps.value as string;
            // setSearchValue(value);
            // setIsShowSearchValue(false);
          }}
          // onDropdownVisibleChange={(e) => {
          //   if (e) {
          //     setFormattedOptions(formattedCityOptions);
          //   }
          // }}
          open={autoCompleteProps.open || !!(searchValue && isShowSearchValue)}
          // value={isShowSearchValue ? searchValue : undefined}
          value={searchValue}
          options={formattedOptions}
          autoFocus
        >
          {isShowSearchValue ? (
            searchValue
          ) : (
            <div
              onClick={() => {
                setIsShowSearchValue(true);
              }}
            >
              {selectedSearchElement?.label?.props?.name ?? t.searchDestination}
            </div>
          )}
        </AutoComplete>
      </div>
      <ArrowSVG />
    </div>
  );
};

export default CustomAutoCompleteCity;
